import React, { createContext, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { supabase, getProdSupabaseClient } from '@/lib/supabase/client';
import { logSecurityEvent } from '@/lib/security/monitoring';
import type { Tenant } from '@/services/api/tenants';
import { tenantAPI } from '@/services/api/tenants';

// Cache to prevent duplicate tenant context loads
interface TenantLoadCache {
  promise: Promise<void>;
  timestamp: number;
}
let tenantLoadCache: TenantLoadCache | null = null;
const TENANT_LOAD_CACHE_TTL_MS = 2000; // 2 second cache

interface TenantContextValue {
  currentTenant: Tenant | null;
  availableTenants: Tenant[];
  switchTenant: (tenantId: string) => Promise<void>;
  refreshTenantContext: () => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

interface TenantProviderProps {
  children: React.ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { user, session, refreshUserProfile } = useAuth();
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Extract tenant information from JWT claims
  const extractTenantFromJWT = () => {
    if (!session?.access_token) {
      console.warn('No access token in session');
      return null;
    }

    try {
      // Parse JWT to get claims
      const payload = session.access_token.split('.')[1];
      if (!payload) {
        console.warn('JWT payload is empty');
        return null;
      }

      const claims = JSON.parse(atob(payload));

      // Debug: Log JWT structure
      if (!claims.tenant_id) {
        console.warn('JWT claims missing tenant_id. Available keys:', Object.keys(claims));
        console.warn('Full claims (sanitized):', {
          ...claims,
          // Don't log sensitive data
          sub: claims.sub ? '***' : undefined,
          email: claims.email ? '***' : undefined
        });
      }

      return {
        tenantId: claims.tenant_id,
        tenantSlug: claims.tenant_slug,
        tenantRole: claims.tenant_role,
        permissions: claims.permissions || [],
        allTenants: claims.tenants || []
      };
    } catch (error) {
      console.error('Failed to parse JWT claims:', error);
      logSecurityEvent('jwt_parse_error', { error: (error as Error).message });
      return null;
    }
  };

  // Load tenant context from JWT and database
  const loadTenantContext = async () => {
    if (!user?.id) {
      setCurrentTenant(null);
      setAvailableTenants([]);
      setIsLoading(false);
      return;
    }

    // Check if there's a recent cached load to prevent duplicate calls
    const now = Date.now();
    if (tenantLoadCache && now - tenantLoadCache.timestamp < TENANT_LOAD_CACHE_TTL_MS) {
      // Wait for existing load to complete
      await tenantLoadCache.promise;
      return;
    }

    // Create promise and cache it
    const loadPromise = (async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get tenant info from JWT
        const jwtTenantInfo = extractTenantFromJWT();

        if (!jwtTenantInfo?.tenantId) {
          // Log the JWT structure for debugging
          if (session?.access_token) {
            try {
              const payload = session.access_token.split('.')[1];
              const claims = JSON.parse(atob(payload));
              console.warn('JWT claims structure:', {
                has_tenant_id: !!claims.tenant_id,
                has_tenant_slug: !!claims.tenant_slug,
                has_tenant_role: !!claims.tenant_role,
                claims_keys: Object.keys(claims),
                full_claims: claims
              });
            } catch (e) {
              console.error('Failed to parse JWT for debugging:', e);
            }
          }

          // SECURITY: No database fallback allowed - enforce JWT-only tenant context
          // This is part of Phase 2.2 of Gold Standard Security Architecture
          logSecurityEvent('missing_tenant_jwt_claims', {
            userId: user.id,
            severity: 'critical',
            message: 'JWT missing tenant_id claim - forcing re-authentication'
          });

          console.error(
            'CRITICAL: JWT missing tenant_id claim. This indicates the custom_access_token_hook is not activated or not working correctly.'
          );
          console.error(
            'Action required: Verify hook is enabled in Supabase Dashboard → Authentication → Hooks'
          );

          // Force re-authentication to get proper JWT with tenant claims
          setError(new Error('Missing tenant context in JWT. Please sign in again.'));
          setIsLoading(false);

          // Sign out and redirect to auth page
          await supabase.auth.signOut();
          // Small delay to ensure state is cleared
          setTimeout(() => {
            window.location.href = '/auth';
          }, 500);

          return;
        }

        // SECURITY: Use JWT claims for tenant_id (validated and secure)
        // Then fetch actual tenant_name from prod.tenants to ensure it matches control center
        // This is safe because we're using the tenant_id from the validated JWT

        // Fetch current tenant data from database to get the actual tenant_name
        const currentTenantData = await tenantAPI.getTenantById(jwtTenantInfo.tenantId);

        // Fallback to JWT data if database fetch fails (shouldn't happen, but safe fallback)
        const tenantFromJWT: Tenant = currentTenantData || {
          id: jwtTenantInfo.tenantId,
          tenant_name: jwtTenantInfo.tenantSlug
            ? jwtTenantInfo.tenantSlug.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            : 'Unknown Tenant',
          tenant_slug: jwtTenantInfo.tenantSlug || '',
          name: jwtTenantInfo.tenantSlug
            ? jwtTenantInfo.tenantSlug.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            : 'Unknown Tenant',
          slug: jwtTenantInfo.tenantSlug || '',
          status: 'active',
          active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        setCurrentTenant(tenantFromJWT);

        // Fetch available tenants from database to get actual tenant_name for each
        if (jwtTenantInfo.allTenants && jwtTenantInfo.allTenants.length > 0) {
          // Fetch all tenant data in parallel
          const tenantDataPromises = jwtTenantInfo.allTenants.map((t: any) =>
            tenantAPI.getTenantById(t.id)
          );
          const tenantDataResults = await Promise.all(tenantDataPromises);

          const availableTenantsFromJWT: Tenant[] = jwtTenantInfo.allTenants.map(
            (t: any, index: number) => {
              // Use database data if available, otherwise fallback to JWT data
              const dbTenant = tenantDataResults[index];
              if (dbTenant) {
                return dbTenant;
              }

              // Fallback to JWT data
              const tName =
                t.name ||
                t.slug?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) ||
                'Unknown Tenant';
              return {
                id: t.id,
                tenant_name: tName,
                tenant_slug: t.slug || '',
                name: tName, // Alias
                slug: t.slug || '', // Alias
                status: t.status || 'active',
                active: t.status !== 'inactive',
                created_at: t.created_at || new Date().toISOString(),
                updated_at: t.updated_at || new Date().toISOString()
              };
            }
          );
          setAvailableTenants(availableTenantsFromJWT);
        } else {
          // If no allTenants in JWT, use current tenant
          setAvailableTenants([tenantFromJWT]);
        }
      } catch (err) {
        console.error('Failed to load tenant context:', err);
        setError(err as Error);
        logSecurityEvent('tenant_context_load_error', {
          userId: user.id,
          error: (err as Error).message
        });
        // Clear cache on error so retry can happen
        tenantLoadCache = null;
      } finally {
        setIsLoading(false);
      }
    })();

    // Cache the promise
    tenantLoadCache = { promise: loadPromise, timestamp: now };

    await loadPromise;
  };

  // Switch to a different tenant
  const switchTenant = async (tenantId: string) => {
    if (!user?.id || !session) {
      throw new Error('No authenticated user');
    }

    try {
      setIsLoading(true);

      // Verify user has access to the requested tenant
      const hasAccess = availableTenants.some(t => t.id === tenantId);
      if (!hasAccess) {
        logSecurityEvent('unauthorized_tenant_switch_attempt', {
          userId: user.id,
          requestedTenantId: tenantId,
          availableTenantIds: availableTenants.map(t => t.id)
        });
        throw new Error('Unauthorized tenant access');
      }

      // Call backend to switch tenant and get new JWT
      const { data, error } = await supabase.functions.invoke('switch-tenant', {
        body: { tenantId }
      });

      if (error) {
        throw error;
      }

      if (!data?.access_token || !data?.refresh_token) {
        throw new Error('Invalid response from tenant switch');
      }

      // Update session with new tokens
      await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token
      });

      // Refresh profile to get updated permissions
      await refreshUserProfile();

      // Log successful tenant switch
      logSecurityEvent('tenant_switch_success', {
        userId: user.id,
        fromTenantId: currentTenant?.id,
        toTenantId: tenantId
      });

      // Reload the page to ensure all contexts are updated
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch tenant:', err);
      setError(err as Error);
      logSecurityEvent('tenant_switch_error', {
        userId: user.id,
        requestedTenantId: tenantId,
        error: (err as Error).message
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh tenant context
  const refreshTenantContext = async () => {
    await loadTenantContext();
  };

  // Load tenant context when auth state changes
  useEffect(() => {
    if (user?.id && session) {
      loadTenantContext();
    } else {
      setCurrentTenant(null);
      setAvailableTenants([]);
      setIsLoading(false);
    }
  }, [user?.id, session?.access_token]);

  // Validate tenant context on every request
  useEffect(() => {
    if (!currentTenant || !session) return;

    // Set up request interceptor to validate tenant context
    const validateTenantContext = () => {
      const jwtInfo = extractTenantFromJWT();
      if (jwtInfo?.tenantId !== currentTenant.id) {
        logSecurityEvent('tenant_context_mismatch', {
          jwtTenantId: jwtInfo?.tenantId,
          contextTenantId: currentTenant.id
        });
        // Force reload to sync contexts
        loadTenantContext();
      }
    };

    // Validate on window focus
    window.addEventListener('focus', validateTenantContext);

    return () => {
      window.removeEventListener('focus', validateTenantContext);
    };
  }, [currentTenant, session]);

  const value = useMemo(
    () => ({
      currentTenant,
      availableTenants,
      switchTenant,
      refreshTenantContext,
      isLoading,
      error
    }),
    [currentTenant, availableTenants, isLoading, error]
  );

  return (
    <TenantContext.Provider value={value}>
      <TenantBoundary tenantId={currentTenant?.id}>{children}</TenantBoundary>
    </TenantContext.Provider>
  );
};

// Tenant boundary component for additional security
interface TenantBoundaryProps {
  tenantId: string | undefined;
  children: React.ReactNode;
}

const TenantBoundary: React.FC<TenantBoundaryProps> = ({ tenantId, children }) => {
  // This component can be used to enforce tenant-level security policies
  // For now, it just passes through children but can be enhanced with additional checks
  return <>{children}</>;
};

// Hook to use tenant context
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

// Helper hook to get current tenant ID
export const useCurrentTenantId = (): string | null => {
  const { currentTenant } = useTenant();
  return currentTenant?.id || null;
};

// Helper hook to check if user has access to multiple tenants
export const useIsMultiTenant = (): boolean => {
  const { availableTenants } = useTenant();
  return availableTenants.length > 1;
};
